import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import deprecated from '../../utils/deprecated'

/**
 * Bronson InstructionStepper component.
 *
 * @param {boolean} [center] - Center-align the whole stepper.
 * @param {InstructionStepperStep[]} [children] - A collection of steps as {@link InstructionStepperStep}.
 * @param {string} [className] - Additional classes on the list element.
 * @param {boolean} [displayIcons=true] - Whether the {@link InstructionStepperStep} should display an icon. Has no effect when `numbered` is set. Default: true
 * @param {boolean} [numbered] - Display numbers for each step. Disables the display icons inside the steps.
 * @param {boolean} [small] - Display a compact version of the {@link InstructionStepper}.
 * @param {string} [testId] - Value for `[data-testid]` on the list element.
 * @param {boolean} [reversed] - Display the steps reversed horizontally.
 * @param {boolean} [vertical] - Display the steps vertically instead of horizontally.
 * @param {string} [otherProps] - Additional props to be passed to the children.
 * @return {JSX.Element} - The {@link InstructionStepper} component.
 * @constructor
 */
export function InstructionStepper({
  center,
  children,
  className,
  displayIcons = true,
  numbered,
  small,
  reversed,
  testId,
  vertical,
  ...otherProps /* in <ol> tag */
}) {
  const olClassNameList = classNames(
    'c-instruction-stepper ',
    {
      'c-instruction-stepper--numbered ': numbered, // Convenience prop from Bronson variants.
      'c-instruction-stepper--vertical ': vertical, // Convenience prop from Bronson variants.
      'c-instruction-stepper--center ': center, // Convenience prop from Bronson variants.
      'c-instruction-stepper--small ': small, // Convenience prop from Bronson variants.
      'c-instruction-stepper--reversed ': reversed, // Convenience prop from Bronson variants.
    },
    className
  ).trim()

  /**
   * Determine whether this stepper should display icons.
   * The `numbered` prop overrules the `displayIcons` prop to avoid number/icon collisions.
   * @type {boolean}
   */
  const displayIcon = !numbered && displayIcons

  // generated main result
  return (
    <ol {...otherProps} data-testid={testId} className={olClassNameList}>
      {/* Set icons explicitly via {@link displayIcon}. */}
      {
        children.map((child) =>
          React.cloneElement(child, { displayIcon })
        ) /* Use {@link InstructionStepperStep} component. */
      }
    </ol>
  )
}

InstructionStepper.propTypes = {
  children: PropTypes.node, // Bronson template: 'instruction-stepper-step'. Use {@link InstructionStepperStep} component.
  className: PropTypes.string, // Bronson template: 'instruction-stepper-modifier'.
  displayIcons: PropTypes.bool, // Bronson template: 'instruction-stepper-display-icons'.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
  /* Convenience Props */
  center: PropTypes.bool, // Convenience prop for c-instruction-stepper--center (Bronson template: 'instruction-stepper-modifier').
  numbered: PropTypes.bool, // Convenience prop for c-instruction-stepper--numbered (Bronson template: 'instruction-stepper-modifier').
  small: PropTypes.bool, // Convenience prop for c-instruction-stepper--small (Bronson template: 'instruction-stepper-modifier').
  reversed: PropTypes.bool, // Convenience prop for c-instruction-stepper--reversed (Bronson template: 'instruction-stepper-modifier').
  vertical: PropTypes.bool, // Convenience prop for c-instruction-stepper--vertical (Bronson template: 'instruction-stepper-modifier').
}

/**
 * Bronson InstructionStepperStep component (nested).
 * @param {string} [state] - Set active state with `is-active`.
 * @param {string} [icon] - The name of the icon to display.
 * @param {string} [title] - The content for the step title.
 * @param {boolean} displayIcons - Deprecated: Please use the `displayIcons` on the parent {@link InstructionStepper} component to display icons.
 * @param {React.ReactNodeArray} children - Children to render inside as the step’s text content.
 * @param {object} otherProps - Additional properties on the element.
 * @return {JSX.Element} - Return the {@link InstructionStepperStep} component.
 * @constructor
 */
function InstructionStepperStep({
  children,
  displayIcon,
  displayIcons,
  icon,
  state,
  title,
  ...otherProps /* in <li> tag */
}) {
  const liClassNameList = classNames('c-instruction-stepper__step ', { [`${state} `]: state }).trim()

  const iClassNameList = classNames('c-icon ', `c-icon--[${icon}]`).trim()

  function renderIfDisplayIcons() {
    if (displayIcon ?? displayIcons) {
      return <i className={iClassNameList} aria-hidden="true" role="img" />
    }
    return null
  }

  function renderIfDescription() {
    if (children) {
      return <span className="c-instruction-stepper__description">{children}</span>
    }
    return null
  }

  return (
    <li {...otherProps} className={liClassNameList}>
      <figure className="c-instruction-stepper__item">{renderIfDisplayIcons()}</figure>
      <div className="c-instruction-stepper__detail">
        <span className="c-instruction-stepper__title">{title}</span>
        {renderIfDescription()}
      </div>
    </li>
  )
}

InstructionStepperStep.propTypes = {
  children: PropTypes.node, // Bronson template: 'description'.
  displayIcon: PropTypes.bool,
  displayIcons: deprecated(
    PropTypes.bool,
    'Displaying icons on single steps is not encouraged. Please use `displayIcons` on the parent {@link InstructionStepper} component instead on singular {@link InstructionStepperStep} items. Prop will be removed in Bronson-React v6.0.0.'
  ), // Deprecated since Bronson 8.6.0
  icon: PropTypes.string, // Bronson template: 'icon'.
  state: PropTypes.string, // Bronson template: 'state'.
  title: PropTypes.string, // Bronson template: 'title'.
}

InstructionStepperStep.displayName = 'InstructionStepper.Step'
InstructionStepper.Step = InstructionStepperStep
